import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const PricingSection: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  const plans = [
    {
      name: 'Admin',
      description: 'Ideal for simple routine workflow, such as data entry, in HR, Finance, Operation, and any Admin department.',
      monthlyPrice: 199,
      annualPrice: 159, // 20% discount
      features: [
        'Multi-step workflows for accurate data entry',
        '7/24 monitoring and executions',
        'No-code visual workflow builder',
        'Unlimited users'
      ],
      stats: {
        credits: '20k',
        creditsDesc: 'agent credits with unlimited workflows',
        pages: '2k',
        pagesDesc: 'pages of document reading and unlimited webpage reading'
      },
      hosting: 'Hosted by Energent.ai',
      buttonText: 'Start free trial'
    },
    {
      name: 'Executive',
      description: 'Functional as a team of Engineers, Analysts and Admins to get work done',
      monthlyPrice: 499,
      annualPrice: 399, // 20% discount
      features: [
        'Coding for Data Engineering/Cleaning Tasks',
        'Up to 10K Document Pages Extraction',
        'Up to 10G Storage',
        'Customized Routine Workflows',
        'Dedicated support with SLA',
        'Invoice billing'
      ],
      stats: {
        credits: '100k',
        creditsDesc: 'agent credits with unlimited workflows',
        pages: '10k',
        pagesDesc: 'pages of document reading and unlimited webpage reading'
      },
      hosting: 'Hosted by Energent.ai',
      buttonText: 'Start free trial',
      includesAdmin: true
    },
    {
      name: 'Enterprise',
      description: 'Ideal for businesses with strict security and performance requirements',
      monthlyPrice: null,
      annualPrice: null,
      customPrice: 'Contact us',
      features: [
        'Unlimited shared projects',
        '100+ concurrent executions',
        'SSO SAML and LDAP',
        'Multiple environments',
        'External secret store integration',
        'Log streaming',
        'Version control using Git',
        'Scaling options',
        'Extended data retention'
      ],
      stats: {
        credits: 'up to ∞',
        creditsDesc: 'workflow executions with unlimited steps',
        pages: 'up to ∞',
        pagesDesc: 'active workflows and unlimited test ones'
      },
      hosting: 'Self-hosted (or hosted by Energent.ai)',
      buttonText: 'Contact us',
      includesExecutive: true
    }
  ];

  return (
    <section className="py-24 bg-black relative overflow-hidden">
      <div className="container mx-auto px-6">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Pricing</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Three different subscriptions to match your companies' needs.
          </p>

          {/* Pricing Toggle */}
          <div className="flex items-center justify-center space-x-4">
            <span className={`text-sm ${!isAnnual ? 'text-white' : 'text-gray-400'}`}>Monthly</span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className="relative w-14 h-7 bg-gray-700 rounded-full transition-colors duration-300 focus:outline-none"
            >
              <motion.div
                className="absolute top-1 w-5 h-5 bg-blue-500 rounded-full transition-transform duration-300"
                animate={{ x: isAnnual ? 32 : 4 }}
              />
            </button>
            <span className={`text-sm ${isAnnual ? 'text-white' : 'text-gray-400'}`}>Annually</span>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 hover:border-gray-700 transition-all duration-300"
            >
              {/* Plan Header */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-white mb-4">{plan.name}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">
                  {plan.description}
                </p>

                {/* Price */}
                <div className="mb-6">
                  {plan.customPrice ? (
                    <div className="text-3xl font-bold text-white">{plan.customPrice}</div>
                  ) : (
                    <div className="text-3xl font-bold text-white">
                      ${isAnnual ? plan.annualPrice : plan.monthlyPrice}
                      <span className="text-gray-400 text-base font-normal"> per month</span>
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-white">{plan.stats.credits}</div>
                      <div className="text-gray-400 text-xs">{plan.stats.creditsDesc}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-white">{plan.stats.pages}</div>
                      <div className="text-gray-400 text-xs">{plan.stats.pagesDesc}</div>
                    </div>
                  </div>
                </div>

                {/* Hosting */}
                <div className="flex items-center space-x-2 text-gray-400 text-sm mb-6">
                  <div className="w-4 h-4 rounded border border-gray-600 flex items-center justify-center">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  </div>
                  <span>{plan.hosting}</span>
                </div>

                {/* CTA Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors duration-200 mb-8"
                >
                  {plan.buttonText}
                </motion.button>
              </div>

              {/* Features */}
              <div>
                <h4 className="text-white font-medium mb-4">
                  {plan.includesExecutive 
                    ? 'Everything in the Executive plan, plus:' 
                    : plan.includesAdmin 
                    ? 'Everything in the Admin plan, plus:'
                    : 'This plan includes:'
                  }
                </h4>
                <ul className="space-y-3">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start space-x-3">
                      <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Floating Animation Elements */}
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-blue-400/40 rounded-full animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/3 w-1 h-1 bg-cyan-400/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 right-1/4 w-1.5 h-1.5 bg-blue-300/50 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>
    </section>
  );
};

export default PricingSection;